import type { fetchFeaturesFamilyMembersOrThrow } from "../../api/fetch.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import type { MiniFamily } from "../../synteny-worker/state.ts";
import type { TrackGroups, Tracks, TrackType } from "../../types.ts";
import type { MemberItem } from "../align.ts";
import { buildFamilyFromFamilyData } from "./left-most-family.ts";

export interface ComputeSyntenyTrackAlignmentsData {
  fromMemberItem: MemberItem;
  trackType: TrackType | undefined;
  trackGroups: TrackGroups;
  tracks: Tracks;
  alignedFamily: MiniFamily | undefined;
  newTrackGroups: ContigSwapUpdate[];
  familyMembers: Awaited<ReturnType<typeof fetchFeaturesFamilyMembersOrThrow>>;
}

export interface ContigSwapUpdate {
  groupId: string;
  newContigId: number;
  toBase?: number;
}

export interface ComputeSyntenyTrackAlignmentsResult {
  newAlignedFamily: MiniFamily | undefined;
  newAlignedTrackGroupIds: string[];
}

export function computeSyntenyTrackAlignments({
  fromMemberItem,
  trackType,
  trackGroups,
  tracks,
  alignedFamily,
  newTrackGroups,
  familyMembers,
}: ComputeSyntenyTrackAlignmentsData): ComputeSyntenyTrackAlignmentsResult {
  let newAlignedFamily: MiniFamily | undefined;
  const newAlignedTrackGroupIds: string[] = [];

  // No family
  if (familyMembers.length === 0) {
    return { newAlignedFamily, newAlignedTrackGroupIds };
  }

  const isSameFamily = alignedFamily?.familyId === fromMemberItem.familyId;

  const currentlyAlignedTrackGroupIds = new Set<string>();
  if (isSameFamily && alignedFamily?.members) {
    for (const member of alignedFamily.members) {
      const track = tracks.get(member.trackId);
      if (track) {
        currentlyAlignedTrackGroupIds.add(track.groupId);
      }
    }
  }

  if (isSameFamily) {
    newAlignedFamily = buildFamilyFromFamilyData(
      familyMembers,
      fromMemberItem,
      tracks,
      trackGroups,
      trackType,
      alignedFamily,
    );
  } else {
    newAlignedFamily = buildFamilyFromFamilyData(familyMembers, fromMemberItem, tracks, trackGroups, trackType);
  }

  const trackIdToOrthologId = new Map<string, number>();
  if (newAlignedFamily?.members) {
    for (const member of newAlignedFamily.members) {
      trackIdToOrthologId.set(member.trackId, member.featureId);
    }
  }

  const memberPositions = new Map<number, number>();
  for (const organism of familyMembers) {
    for (const contig of organism.contigs) {
      for (const member of contig.members) {
        memberPositions.set(member.locationId, member.start);
      }
    }
  }

  const activeBaseShift =
    trackGroups.get(tracks.get(fromMemberItem.trackId)?.groupId ?? "--unknown--")?.graphics.baseShift ?? 0;

  for (const [groupId, group] of trackGroups) {
    const track = Array.from(tracks.values()).find(
      (track) => track.groupId === groupId && track.trackType === trackType,
    );

    if (!track) continue;

    const assignedOrthologId = trackIdToOrthologId.get(track.trackId);

    if (assignedOrthologId) {
      if (!newAlignedTrackGroupIds.includes(groupId)) {
        newAlignedTrackGroupIds.push(groupId);
      }

      const assignedPosition = memberPositions.get(assignedOrthologId);

      if (assignedPosition != undefined) {
        if (fromMemberItem.trackId !== track.trackId) {
          const baseShift = activeBaseShift + (fromMemberItem.start ?? 0) - assignedPosition;
          group.graphics.baseShift = baseShift;
        }
      }
    } else {
      if (!(isSameFamily && currentlyAlignedTrackGroupIds.has(groupId))) {
        const swapUpdate = newTrackGroups.find((g) => g.groupId === group.groupId);
        if (swapUpdate?.toBase != undefined) {
          group.graphics.baseShift = activeBaseShift + (fromMemberItem.start ?? 0) - swapUpdate.toBase;
        }
      }
    }
  }

  useGenomeViewerStore.getState().forceOverviewRender();

  return {
    newAlignedFamily,
    newAlignedTrackGroupIds,
  };
}
